import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { fileSourcesApi } from '../services/api';

function FileSourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const sourceRes = await fileSourcesApi.get(id);
      setSource(sourceRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    setError(null);
    
    for (const file of acceptedFiles) {
      try {
        await fileSourcesApi.uploadFile(id, file);
      } catch (error) {
        console.error('Error uploading file:', error);
        setError(`Erro ao fazer upload de ${file.name}`);
      }
    }
    
    setUploading(false);
    loadData();
  }, [id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
    },
  });

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Tem certeza que deseja excluir este arquivo?')) return;
    
    try {
      await fileSourcesApi.deleteFile(id, fileId);
      loadData();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!source) {
    return (
      <Box>
        <Alert severity="error">Fonte de dados não encontrada</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/file-sources')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/file-sources')}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">{source.name}</Typography>
          <Typography color="textSecondary">{source.description || 'Sem descrição'}</Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Area */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <CircularProgress />
        ) : (
          <>
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6">
              {isDragActive
                ? 'Solte os arquivos aqui'
                : 'Arraste arquivos ou clique para fazer upload'}
            </Typography>
            <Typography color="textSecondary">
              Suporta PDF, TXT, DOCX, EML e MSG (Outlook)
            </Typography>
          </>
        )}
      </Paper>

      {/* Files List */}
      {source.files?.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Arquivo</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Tamanho</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {source.files.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FileIcon color="primary" />
                      {file.original_filename}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={file.file_type} size="small" />
                  </TableCell>
                  <TableCell>{formatFileSize(file.file_size)}</TableCell>
                  <TableCell>
                    {new Date(file.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteFile(file.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Nenhum arquivo carregado ainda
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FileSourceDetail;
